import { formatDateTimeIn, formatMoneyIn, toLocale } from '@zuruflex/i18n';
import {
  HANDOVER_PHOTO_KINDS,
  handoverItemKey,
  type BookingHandover,
  type FuelLevel,
  type HandoverPhotoKind,
} from '@zuruflex/types';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  FormField,
  Skeleton,
  Textarea,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import {
  HANDOVER_CHECKS,
  HANDOVER_PHOTO_LABEL,
  HandoverPhotoGrid,
  FuelField,
  OdometerField,
  SectionLabel,
  TickRow,
  describeFailure,
  photosFromRecord,
  uploadHandoverPhoto,
  useHandoverPhotos,
  useKm,
  type HandoverBooking,
  type HandoverCheck,
} from './handover-fields.js';

export interface HandoverDialogProps {
  reference: string;
  onClose: () => void;
}

/**
 * Handing a car over, from the portal.
 *
 * This replaced a button that flipped CONFIRMED to ACTIVE and recorded
 * nothing — so a hire started on the web went out with no reading, no
 * gauge and no photographs, and the deposit argument a fortnight later had
 * nothing to stand on. It is the phone's three screens (`handover/checks`,
 * `handover/record`) folded into one dialog, because a desk has the room
 * for all of it at once and the phone does not.
 *
 * The rules are the phone's: all four checks, a reading, a mark on the
 * gauge and all four angles before the button wakes up. The car's own
 * items are listed but never gate it — a partner who forgot the child seat
 * is still handing the car over, and the record should say so rather than
 * refuse to exist.
 */
export function HandoverDialog({ reference, onClose }: HandoverDialogProps) {
  const { t } = useTranslation(['partner', 'common']);

  const booking = useQuery({
    queryKey: ['booking', reference],
    queryFn: () => api.getBooking(reference) as Promise<HandoverBooking>,
  });

  /*
   * Read every time the dialog opens, because the answer changes what it
   * is: no pick-up record means a fresh handover, and one on a booking that
   * is still CONFIRMED means a commit that wrote the record and then failed
   * — the upload on a bad connection, or the status move — and the partner
   * has come back to finish it.
   */
  const handovers = useQuery({
    queryKey: ['handovers', reference],
    queryFn: () => api.listBookingHandovers(reference),
  });

  const failure = booking.error ?? handovers.error;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-2xl">
        {failure ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('handover.app.record.submit')}</DialogTitle>
            </DialogHeader>
            <ErrorState
              error={failure}
              onRetry={() => {
                void booking.refetch();
                void handovers.refetch();
              }}
            />
          </>
        ) : booking.data && handovers.data ? (
          /*
           * Mounted once both have landed, so the form's state can be
           * seeded from them in its initialisers rather than by an effect
           * that would race the partner's first keystroke. Not keyed on
           * the data: a refetch after a failed commit must not wipe what
           * is on screen.
           */
          <PickupForm
            booking={booking.data}
            record={handovers.data.items.find((item) => item.kind === 'PICKUP')}
            onClose={onClose}
          />
        ) : (
          <div className="space-y-4" aria-busy>
            <DialogHeader>
              <DialogTitle>{t('handover.app.record.submit')}</DialogTitle>
              <DialogDescription>{t('common:state.loading')}</DialogDescription>
            </DialogHeader>
            <Skeleton className="h-40 rounded-card" />
            <Skeleton className="h-28 rounded-card" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PickupForm({
  booking,
  record,
  onClose,
}: {
  booking: HandoverBooking;
  /** An unfinished pick-up, written by a commit that did not get to the end. */
  record: BookingHandover | undefined;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const queryClient = useQueryClient();
  const km = useKm();
  const reference = booking.reference;
  const vehicle = booking.vehicle ?? null;

  /*
   * The car's list as it stands today, plus anything the unfinished record
   * names that has since come off it — that item may well be sitting in
   * the renter's boot, and dropping it from a correction would make the
   * record forget it went out.
   */
  const [labels] = useState<string[]>(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const label of [
      ...(vehicle?.handoverItems ?? []),
      ...(record?.items.map((item) => item.label) ?? []),
    ]) {
      const key = handoverItemKey(label);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(label);
    }
    return out;
  });

  const [checks, setChecks] = useState<Record<HandoverCheck, boolean>>(() => {
    const ticked = new Set(record?.checklist ?? []);
    return {
      licence: ticked.has('licence'),
      deposit: ticked.has('deposit'),
      kit: ticked.has('kit'),
      rules: ticked.has('rules'),
    };
  });

  /*
   * Unticked on a fresh handover, as on the phone: a tick is the partner
   * saying they saw the child seat go into the car, and a list that arrived
   * pre-ticked would record "went out" for things nobody looked for — the
   * exact claim a deposit dispute over a missing item rests on. A resumed
   * one keeps what was said the first time.
   */
  const [out, setOut] = useState<Set<string>>(
    () =>
      new Set(
        (record?.items ?? [])
          .filter((item) => item.present)
          .map((item) => handoverItemKey(item.label)),
      ),
  );

  const [odometer, setOdometer] = useState(record ? String(record.odometerKm) : '');
  const [fuel, setFuel] = useState<FuelLevel | null>(record?.fuelLevel ?? null);
  const [notes, setNotes] = useState(record?.notes ?? '');
  const { photos, pick, markFiled, complete } = useHandoverPhotos(photosFromRecord(record));
  const [uploading, setUploading] = useState<HandoverPhotoKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const guest = booking.customer?.fullName ?? t('bookings.unnamedCustomer');
  const car = vehicle ? `${vehicle.make} ${vehicle.model}` : t('common:field.vehicle');
  const allChecked = HANDOVER_CHECKS.every((check) => checks[check]);
  const ready = allChecked && odometer !== '' && fuel !== null && complete;

  const commit = useMutation({
    mutationFn: async () => {
      if (fuel === null) return;

      /*
       * The record first. It is an upsert on (booking, kind), so a second
       * attempt after a failed upload corrects the row it already wrote
       * rather than colliding with it — which is what makes pressing the
       * button again safe.
       */
      await api.recordBookingHandover(reference, {
        kind: 'PICKUP',
        odometerKm: Number(odometer),
        fuelLevel: fuel,
        checklist: HANDOVER_CHECKS.filter((check) => checks[check]),
        // Every item on the list, the ones that stayed behind included:
        // "not provided" is a fact the return check needs as much as
        // "went out" is, or a missing seat reads as a lost one.
        items: labels.map((label) => ({ label, present: out.has(handoverItemKey(label)) })),
        notes: notes.trim() || undefined,
      });

      /*
       * Then the photographs, one at a time and marked off as they land,
       * so a retry after the third one fails sends only the fourth.
       */
      for (const photoKind of HANDOVER_PHOTO_KINDS) {
        const slot = photos[photoKind];
        if (!slot?.file || slot.filed) continue;
        setUploading(photoKind);
        await uploadHandoverPhoto(
          reference,
          slot.file,
          { kind: 'PICKUP', photoKind },
          t('handover.web.uploadFailed', { angle: t(HANDOVER_PHOTO_LABEL[photoKind]) }),
        );
        markFiled(photoKind);
      }
      setUploading(null);

      /*
       * And last the status — the only thing that moves the booking and
       * the only thing that seals the record. Until this returns, the car
       * has not gone out.
       */
      await api.updateBookingStatus(reference, 'ACTIVE');
    },
    onMutate: () => setError(null),
    onError: (cause) => {
      setUploading(null);
      // What did land is kept on screen and on the server, so the sentence
      // says "press it again" rather than "start over".
      setError(
        `${describeFailure(cause, t('common:error.unexpected'))} ${t('handover.web.partWay')}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['handovers', reference] });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['my-bookings'] }),
        queryClient.invalidateQueries({ queryKey: ['booking', reference] }),
        queryClient.invalidateQueries({ queryKey: ['handovers', reference] }),
        queryClient.invalidateQueries({ queryKey: ['my-fleet'] }),
      ]);
      onClose();
    },
  });

  const busy = commit.isPending;

  /** "Self-drive · 150 km/day · back by 6 Oct, 18:00." — the phone's line. */
  const rules = t('handover.app.checks.rulesHint', {
    policy: vehicle ? t(`common:driverPolicy.${vehicle.driverPolicy}`) : '',
    allowance:
      vehicle && vehicle.dailyKmAllowance !== null
        ? t('common:unit.kmPerDay', { count: vehicle.dailyKmAllowance })
        : t('common:unit.unlimited'),
    due: formatDateTimeIn(booking.dropoffAt, locale),
  });

  const toggleItem = (label: string, present: boolean) =>
    setOut((current) => {
      const next = new Set(current);
      if (present) next.add(handoverItemKey(label));
      else next.delete(handoverItemKey(label));
      return next;
    });

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !busy) commit.mutate();
      }}
    >
      <DialogHeader>
        <DialogTitle>{t('handover.web.title', { car })}</DialogTitle>
        <DialogDescription>
          {t('handover.web.description', {
            guest,
            plate: vehicle?.plateNumber ?? '',
            reference,
          })}
        </DialogDescription>
      </DialogHeader>

      {record ? <Alert variant="info">{t('handover.web.resumed')}</Alert> : null}

      <section>
        <SectionLabel>{t('handover.app.checks.heading')}</SectionLabel>
        <div className="mt-1">
          <TickRow
            checked={checks.licence}
            disabled={busy}
            onChange={(next) => setChecks({ ...checks, licence: next })}
            title={t('handover.app.checks.licence')}
          />
          <TickRow
            checked={checks.deposit}
            disabled={busy}
            onChange={(next) => setChecks({ ...checks, deposit: next })}
            title={t('handover.app.checks.deposit')}
            body={t('handover.app.checks.depositHint', {
              amount: formatMoneyIn(booking.depositAmount, booking.currency, locale),
            })}
          />
          <TickRow
            checked={checks.kit}
            disabled={busy}
            onChange={(next) => setChecks({ ...checks, kit: next })}
            title={t('handover.app.checks.kit')}
          />
          <TickRow
            checked={checks.rules}
            disabled={busy}
            onChange={(next) => setChecks({ ...checks, rules: next })}
            title={t('handover.app.checks.rules')}
            body={rules}
          />
        </div>
      </section>

      <section>
        <SectionLabel>{t('handover.web.itemsHeading')}</SectionLabel>
        {labels.length > 0 ? (
          <>
            <p className="mt-1 text-xs text-text-muted">{t('handover.web.itemsHint', { guest })}</p>
            <div className="mt-1">
              {labels.map((label) => (
                <TickRow
                  key={label}
                  checked={out.has(handoverItemKey(label))}
                  disabled={busy}
                  onChange={(next) => toggleItem(label, next)}
                  title={label}
                />
              ))}
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-text-muted">{t('handover.web.itemsNone')}</p>
        )}
      </section>

      <div className="grid gap-5 sm:grid-cols-2">
        <OdometerField
          value={odometer}
          onChange={setOdometer}
          disabled={busy}
          hint={
            /* The reading on file, and no date beside it — see the same
               note in the phone's `handover/record.tsx`. */
            vehicle && vehicle.odometerKm > 0
              ? t('handover.app.record.odometerOnFile', { reading: km(vehicle.odometerKm) })
              : undefined
          }
        />
        <FuelField
          value={fuel}
          onChange={setFuel}
          disabled={busy}
          hint={t('handover.app.record.fuelHint', { guest })}
        />
      </div>

      <HandoverPhotoGrid
        photos={photos}
        onPick={pick}
        busyKind={uploading}
        disabled={busy}
        onRejected={setError}
      />

      <FormField label={t('handover.app.record.marks')}>
        {(field) => (
          <Textarea
            {...field}
            rows={2}
            maxLength={1000}
            disabled={busy}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={t('handover.app.record.marksPlaceholder')}
          />
        )}
      </FormField>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <DialogFooter className="items-center">
        {/* Says what is still missing, so a grey button is not a riddle. */}
        {!ready ? (
          <p className="mr-auto text-xs text-text-muted">{t('handover.web.notReady')}</p>
        ) : null}
        <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
          {t('common:action.cancel')}
        </Button>
        <Button type="submit" disabled={!ready} isLoading={busy}>
          {t('handover.app.record.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}
